import { fetchData, setHeaderOptions } from "#/api/apiHelpers";

export type ArticleStatus = "draft" | "published" | "archived" | string;

export type ArticleListItem = {
  id: string;
  slug: string;
  title: string;
  version: number;
  heroAssetId?: string | null;
  status?: ArticleStatus | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type ArticleSection = {
  id: string;
  key?: string | null;
  slug?: string | null;
  title?: string | null;
  order?: number | null;
};

export type ArticleBlock = {
  id: string;
  key?: string | null;
  sectionId?: string | null;
  type?: string | null;
  text?: string | null;
  content?: string | null;
  markdown?: string | null;
  items?: string[] | null;
  level?: number | null;
  order_index?: number | null;
  text_spans?: Array<{ text?: string | null }> | null;
  data?: Record<string, unknown> | null;
} & Record<string, unknown>;

export type ArticleAsset = {
  id: string;
  assetId?: string;
  sectionId?: string | null;
  placement?: string | { assetUrl?: string | null } | null;
  url?: string | null;
  alt?: string | null;
  type?: string | null;
  asset?: {
    id?: string;
    url?: string | null;
    alt?: string | null;
    basePrompt?: string | null;
    generationPrompt?: string | null;
    generation_prompt?: string | null;
    meta?: Record<string, unknown> | null;
  } | null;
} & Record<string, unknown>;

export type ArticlePayload = {
  article: ArticleListItem;
  sections?: ArticleSection[] | null;
  blocks?: ArticleBlock[] | null;
  assets?: ArticleAsset[] | null;
} & Record<string, unknown>;

export type ArticleRevisionListItem = {
  id: string;
  articleId: string;
  rev: number;
  message: string | null;
  authorId: string | null;
  createdAt: string;
  isPublished: boolean;
};

export type ArticleGenerateTarget = {
  type: string;
  id?: string;
  key?: string | null;
  suggestion: string;
};

export type ArticleGeneratePayload = {
  suggestions?: string;
  instructions?: string;
  articleId?: string;
  article?: Partial<ArticleListItem>;
  sections?: ArticleSection[];
  blocks?: ArticleBlock[];
  assets?: ArticleAsset[];
  entities?: Array<{ entityId: string; entityTypeId: number }>;
  targets?: ArticleGenerateTarget[];
};

const BASE_ROUTE = "/admin/articles";
const DETAIL_ROUTE = "/articles";
const GENERATE_ROUTE = "/article/generate";
const REVISE_ROUTE = "/article/generate/revise";
const ASSET_REVIEW_ROUTE = "/admin/articles/assets";

export type ArticleRevisePayload = {
  articleId: string;
  revisions: string | string[];
  instructions: string;
  model?: string;
  article?: Partial<ArticleListItem>;
  sections?: ArticleSection[];
  blocks?: ArticleBlock[];
  assets?: ArticleAsset[];
  entities?: Array<{
    entityId: string;
    entityTypeId: number;
    relationship_type?: string;
  }>;
  targets?: ArticleGenerateTarget[];
};

export async function listArticles(
  options?: { signal?: AbortSignal }
): Promise<ArticleListItem[]> {
  const request = setHeaderOptions({ method: "GET" });
  return fetchData<ArticleListItem[]>(BASE_ROUTE, {
    ...request,
    signal: options?.signal,
  });
}

export async function getArticleBySlug(
  slug: string,
  options?: { signal?: AbortSignal }
): Promise<ArticlePayload> {
  if (!slug) {
    throw new Error("Article slug is required");
  }

  const request = setHeaderOptions({ method: "GET" });
  return fetchData<ArticlePayload>(
    `${DETAIL_ROUTE}/${encodeURIComponent(slug)}`,
    {
      ...request,
      signal: options?.signal,
    }
  );
}

export async function generateArticle(
  payload: ArticleGeneratePayload
): Promise<unknown> {
  const request = setHeaderOptions({
    method: "POST",
    body: payload,
  });
  return fetchData<unknown>(GENERATE_ROUTE, request);
}

export async function reviseArticle(
  payload: ArticleRevisePayload
): Promise<unknown> {
  const request = setHeaderOptions({
    method: "POST",
    body: payload,
  });
  return fetchData<unknown>(REVISE_ROUTE, request);
}

export async function listArticleRevisions(
  articleId: string
): Promise<ArticleRevisionListItem[]> {
  if (!articleId) {
    throw new Error("Article id is required");
  }

  const request = setHeaderOptions({ method: "GET" });
  return fetchData<ArticleRevisionListItem[]>(
    `${BASE_ROUTE}/${encodeURIComponent(articleId)}/revisions`,
    request
  );
}

export async function rollbackArticleRevision(
  articleId: string,
  revisionId: string
): Promise<{ revisionId: string }> {
  if (!articleId || !revisionId) {
    throw new Error("Article id and revision id are required");
  }

  const request = setHeaderOptions({ method: "POST" });
  return fetchData<{ revisionId: string }>(
    `${BASE_ROUTE}/${encodeURIComponent(articleId)}/revisions/${encodeURIComponent(revisionId)}/rollback`,
    request
  );
}

export async function requestArticleReview(
  articleId: string
): Promise<unknown> {
  if (!articleId) {
    throw new Error("Article id is required");
  }

  const request = setHeaderOptions({
    method: "POST",
  });

  return fetchData<unknown>(
    `${BASE_ROUTE}/${encodeURIComponent(articleId)}/review`,
    request
  );
}

export async function requestArticleReviewRevision(
  articleId: string,
  options?: { reviewIds?: string[] }
): Promise<unknown> {
  if (!articleId) {
    throw new Error("Article id is required");
  }

  const request = setHeaderOptions({
    method: "POST",
    ...(options?.reviewIds?.length
      ? { body: { reviewIds: options.reviewIds } }
      : {}),
  });
  return fetchData<unknown>(
    `${BASE_ROUTE}/review-revision/${encodeURIComponent(articleId)}`,
    request
  );
}

export type ArticleAssetReviewResponse = {
  assetId: string;
  sectionId?: string | null;
  blockId?: string | null;
  status: "pass" | "warn" | "fail";
  notes: string;
  action?: "queued_update" | "skipped";
  queuedUpdate?: {
    sourceAssetId: string;
    newAssetId: string;
  } | null;
};

export type ArticleAssetReviewContext = {
  assetId: string;
  basePrompt: string | null;
  reviewUrl: string | null;
  sectionId?: string | null;
  blockId?: string | null;
  articleId?: string | null;
  hasPlacements: boolean;
};

export async function getArticleAssetReviewContext(
  assetId: string
): Promise<ArticleAssetReviewContext> {
  if (!assetId) {
    throw new Error("Asset id is required");
  }

  const request = setHeaderOptions({ method: "GET" });
  return fetchData<ArticleAssetReviewContext>(
    `${ASSET_REVIEW_ROUTE}/${encodeURIComponent(assetId)}/review`,
    request
  );
}

export async function requestArticleAssetReview(
  assetId: string,
  options?: { note?: string; basePrompt?: string }
): Promise<ArticleAssetReviewResponse> {
  if (!assetId) {
    throw new Error("Asset id is required");
  }

  const note = options?.note?.trim();
  const basePrompt = options?.basePrompt?.trim();
  const request = setHeaderOptions({
    method: "POST",
    ...((note || basePrompt)
      ? {
          body: {
            ...(note ? { note } : {}),
            ...(basePrompt ? { basePrompt } : {}),
          },
        }
      : {}),
  });
  return fetchData<ArticleAssetReviewResponse>(
    `${ASSET_REVIEW_ROUTE}/${encodeURIComponent(assetId)}/review`,
    request
  );
}

export async function deleteArticle(articleId: string): Promise<void> {
  const request = setHeaderOptions({ method: "DELETE" });
  await fetchData<{ success: boolean; articleId: string }>(
    `${BASE_ROUTE}/${encodeURIComponent(articleId)}`,
    request
  );
}
